import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import RateItCreatorRow from './RateItCreatorRow';
import RateItRatingBar from './RateItRatingBar';
import { RATE_IT_COLORS, RATE_IT_CREATOR_SCRIM, RATE_IT_LAYOUT, RATE_IT_SCRIM } from '../constants';

const Anchor = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  /* NO ground of its own: the tinted slab below covers only the tiles, so the caption above it
     sits on the media (over the scrim) rather than on a band. */
`;

/**
 * One scrim reaching above the slab so the fade sits behind the caption, which hangs outside the
 * tinted band. Drawn here rather than on the bar so it covers the whole stack.
 */
const Scrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 220px;
  background: ${RATE_IT_SCRIM};
  pointer-events: none;
`;

/**
 * The caption and the creator row, over the creator's own scrim.
 *
 * The gap to the slab is PADDING rather than the row's margin so the scrim, which fills this
 * block, runs all the way down to the slab's top edge with no light seam between them.
 */
const Top = styled.div`
  position: relative;
  padding-bottom: ${RATE_IT_LAYOUT.CREATOR_ROW_GAP}px;
`;

/** The creator row's dark fade — mobile's `RATE_IT_CREATOR_SCRIM_*`, as Tag It's row gets. */
const CreatorScrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: -${RATE_IT_LAYOUT.CREATOR_SCRIM_REACH}px;
  bottom: 0;
  background: ${RATE_IT_CREATOR_SCRIM};
  pointer-events: none;
`;

const CreatorWrap = styled.div`
  position: relative;
  padding: 0 ${RATE_IT_LAYOUT.BAR_GUTTER}px;
`;

/**
 * The instruction, deliberately OUTSIDE the slab.
 *
 * It labels the tiles but is not one of them, and putting it inside the tinted container made
 * the band read as a single block of chrome roughly twice as tall as the controls it holds. Out
 * here it sits on the moment, over the scrim, exactly as the design has it. `position:
 * relative` also lifts it above the creator scrim drawn before it.
 */
const Caption = styled.p`
  position: relative;
  margin: 0 0 ${RATE_IT_LAYOUT.CAPTION_GAP}px;
  /* The creator row's gutter, so the left-aligned text lines up with the avatar and the tiles
     rather than touching the screen edge. */
  padding: 0 ${RATE_IT_LAYOUT.BAR_GUTTER}px;
  /* Left-aligned, sentence case, so no wide tracking — that was for the old all-caps label. */
  text-align: left;
  font-family: CeraPro, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${RATE_IT_COLORS.CAPTION};
`;

const Slab = styled.div`
  position: relative;
  background-color: ${RATE_IT_COLORS.SLAB_BG};
  /* The home indicator on iOS Safari. */
  padding-bottom: env(safe-area-inset-bottom, 0px);

  /*
   * Above the breakpoint the pager puts its "next moment" control at the bottom centre, which
   * landed on the middle tile. The tiles lift clear of it here rather than the control moving,
   * because the control belongs on the moment's centre line and the tiles have room to give.
   *
   * The lift is PADDING, not a bottom offset on the anchor: the slab must still reach the
   * bottom edge, or the gap would show a strip of media under a band that reads as the floor.
   * The chevron then sits inside that padding, on the slab, where it has a ground of its own.
   */
  @media (min-width: ${RATE_IT_LAYOUT.DESKTOP_BREAKPOINT}px) {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + ${RATE_IT_LAYOUT.CHEVRON_CLEARANCE}px);
  }
`;

/**
 * The bottom of a feed card: the scrim, the instruction, the creator row, and the tinted slab
 * carrying the tiles — in that order top to bottom, as on mobile. The creator row sits on the
 * moment (over its own scrim), outside the slab, where FanCam's player keeps its creator row.
 *
 * THE WEB FEED HAS NO SHARE AFFORDANCE. Mobile's card carries the brief's §9 persistent share
 * button and an action row to hold it, and this component originally existed to own that row.
 * On web the module is a referral LANDING rather than a place to start new invites: the referee
 * rates, and the reward settles server-side. So the action row is gone.
 *
 * The three layers stay split across this file and `RateItRatingBar` along one line: this owns
 * everything about WHERE the bar sits and what it sits on, the bar owns the tiles themselves.
 *
 * The caption is shown only while the item is UNRATED. It is an instruction, and once the fan
 * has rated there is nothing left to instruct — the filled tile is the state now, and a prompt
 * still sitting over it would read as if the tap had not registered.
 */
const RateItFeedBottomStack = ({ selected, disabled, onRate, creator, viewCount, tallies }) => {
  const { t } = useTranslation();

  return (
    <Anchor>
      <Scrim />
      <Top>
        <CreatorScrim />
        {selected === null ? (
          <Caption>
            {t('tiri.rateIt.caption', 'Rate this Fan Moment – Solid, Great, or Epic')}
          </Caption>
        ) : null}
        <CreatorWrap>
          <RateItCreatorRow creator={creator} viewCount={viewCount} />
        </CreatorWrap>
      </Top>
      <Slab>
        <RateItRatingBar
          selected={selected}
          disabled={disabled}
          onRate={onRate}
          tallies={tallies}
        />
      </Slab>
    </Anchor>
  );
};

export default RateItFeedBottomStack;
